/**
 * RichTextEditor — small, focused editor for coach intro pages and
 * session/day notes. Renders B / I / U / bullet / numbered / H2 / H3 in a
 * compact toolbar above a `contentEditable` div. Stores its value as a
 * sanitized HTML string.
 *
 * Design choices:
 * - `document.execCommand` is still the simplest reliable cross-browser way
 *   to handle inline formatting inside a contentEditable. Yes it's deprecated
 *   on paper, but it works in every shipping browser today and avoids the
 *   ~50 KB cost of a full editor library (Tiptap/Lexical) for a 4-field
 *   feature.
 * - We sanitize ONCE on blur (when persisting upstream) and also at render
 *   time via `<RichTextView>` so we never trust untrusted HTML in the DOM.
 * - The `paste` handler strips formatting from external sources (Word /
 *   Google Docs) so coaches don't accidentally drag in 12-color heading
 *   garbage that breaks the PDF render.
 */
import React from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3 } from "lucide-react";

const ALLOWED_TAGS = [
  "b", "strong", "i", "em", "u",
  "h2", "h3",
  "ul", "ol", "li",
  "br", "p", "div",
  "a", "span",
];
const ALLOWED_ATTRS = ["href", "target", "rel"];

export function sanitizeRichHtml(html) {
  if (!html) return "";
  const cleaned = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRS,
    // Auto-target external links to _blank
    ADD_ATTR: ["target", "rel"],
  });
  // Trim trailing "&nbsp;" / whitespace / <br> sequences that
  // contentEditable browsers inject when the user hits Space or Enter
  // after their last real character. Without this cleanup, plain
  // sentences saved from the rich editor render as
  // "Ends with a period.&nbsp;" in downstream <RichTextView> and
  // fallback plain-text views (which don't decode entities).
  return cleaned
    .replace(
      /(?:&nbsp;|\s|<br\s*\/?>)+(?=<\/(?:p|div|li|h[1-6])>|$)/gi,
      "",
    )
    .trim();
}


/**
 * Decode common HTML entities to plain text. Used by the plain-text
 * render branch (WorkoutDetail, athlete session notes) so a legacy /
 * unsanitized value containing `&nbsp;` doesn't render as the literal
 * ampersand-nbsp-semicolon string. We deliberately keep this small and
 * targeted — full-strength entity decoding lives in DOMPurify's HTML
 * path.
 */
export function decodeEntities(text) {
  if (!text) return "";
  return String(text)
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Compact toolbar button — keeps the visual weight low so it sits inside
// the planner day cells without dominating them.
function TbBtn({ onClick, active, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // mousedown (not click) so the contentEditable doesn't lose focus
        // before the command runs.
        e.preventDefault();
        onClick();
      }}
      data-testid={testid}
      aria-label={label}
      title={label}
      className={`h-6 w-6 flex items-center justify-center border border-transparent hover:border-[var(--tir-border)] text-[var(--tir-muted)] hover:text-[var(--tir-ink)] ${
        active ? "bg-[var(--tir-ink)] text-white hover:text-white" : ""
      }`}
    >
      <Icon size={12} />
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  testid,
  toolbarCompact = false,
}) {
  const ref = React.useRef(null);
  // Track focus so we can avoid resetting innerHTML (and yanking the caret
  // to position 0) while the user is actively typing. The parent's
  // autosave round-trip will re-send the same `value` prop a moment later
  // — if we apply that change mid-typing, the cursor jumps to the start.
  const focusedRef = React.useRef(false);
  // Sentinel — NEVER initialize to the incoming `value`. Doing so makes
  // the first useEffect think the editor already shows the saved content
  // and skip the innerHTML assignment, leaving the editor visually empty
  // even though the prop is populated. (That's what caused the wipe-on-
  // first-load bug.) Only gets set after we actually emit something.
  const lastEmittedRef = React.useRef(null);

  // Mount the value into the contentEditable on first render and on every
  // external reset (e.g. loading a different program). We compare against
  // `lastEmittedRef` so React renders triggered by our own `onChange` don't
  // reset innerHTML mid-typing and yank the caret to position 0.
  React.useEffect(() => {
    if (!ref.current) return;
    // If the user is actively typing, never replace innerHTML — even a
    // semantically-equivalent reassignment moves the caret to the start.
    // We'll re-sync once they blur.
    if (focusedRef.current) return;
    const clean = sanitizeRichHtml(value || "");
    if (clean === lastEmittedRef.current) return; // our own echo
    if (ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    if (!ref.current) return;
    ref.current.focus();
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand(cmd, false, arg);
    emit();
  };

  const emit = () => {
    if (!ref.current) return;
    const html = sanitizeRichHtml(ref.current.innerHTML);
    lastEmittedRef.current = html;
    onChange(html);
  };

  // Paste handler — strip formatting from external sources but keep the
  // text content + line breaks. Avoids "Word soup" entering the editor.
  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain") || "";
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand("insertText", false, text);
  };

  // Block the default block-quote behavior of contentEditable so Enter just
  // creates a fresh paragraph inside the current block.
  const onKeyDown = (e) => {
    // Cmd/Ctrl+B / I / U → standard formatting (already wired by the browser)
    if (e.key === "Tab") {
      e.preventDefault();
      // eslint-disable-next-line no-restricted-syntax
      document.execCommand(e.shiftKey ? "outdent" : "indent", false);
    }
  };

  const minHeightPx = Math.max(48, rows * 22 + 16);

  return (
    <div className="border border-[var(--tir-border)] focus-within:border-[var(--tir-burgundy)] bg-white">
      <div
        className={`flex items-center gap-0.5 ${
          toolbarCompact ? "px-1 py-0.5" : "px-2 py-1"
        } border-b border-[var(--tir-border)] bg-[var(--tir-paper)]/60`}
      >
        <TbBtn onClick={() => exec("bold")} icon={Bold} label="Bold (Cmd/Ctrl+B)" testid={`${testid}-bold`} />
        <TbBtn onClick={() => exec("italic")} icon={Italic} label="Italic (Cmd/Ctrl+I)" testid={`${testid}-italic`} />
        <TbBtn onClick={() => exec("underline")} icon={Underline} label="Underline (Cmd/Ctrl+U)" testid={`${testid}-underline`} />
        <span className="w-px h-4 bg-[var(--tir-border)] mx-1" />
        <TbBtn onClick={() => exec("insertUnorderedList")} icon={List} label="Bulleted list" testid={`${testid}-ul`} />
        <TbBtn onClick={() => exec("insertOrderedList")} icon={ListOrdered} label="Numbered list" testid={`${testid}-ol`} />
        <span className="w-px h-4 bg-[var(--tir-border)] mx-1" />
        <TbBtn onClick={() => exec("formatBlock", "<h2>")} icon={Heading2} label="Heading" testid={`${testid}-h2`} />
        <TbBtn onClick={() => exec("formatBlock", "<h3>")} icon={Heading3} label="Sub-heading" testid={`${testid}-h3`} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => {
          focusedRef.current = false;
          // Final emit so the parent has the sanitized HTML; also ensures
          // any normalization the browser applied (lists, etc.) round-trips
          // back through the prop on the NEXT focus instead of mid-typing.
          emit();
        }}
        data-testid={testid}
        data-placeholder={placeholder}
        className="rich-text-editor px-3 py-2 text-sm leading-relaxed focus:outline-none"
        style={{ minHeight: `${minHeightPx}px` }}
      />
    </div>
  );
}

/**
 * Read-only render of a sanitized rich-text value. Use everywhere the
 * coach's intro/session/day notes are displayed (planner cards, PDFs,
 * athlete portal). Empty strings render nothing so callers can wrap with
 * conditional spacing.
 */
export function RichTextView({ html, className = "", testid }) {
  if (!html) return null;
  const safe = sanitizeRichHtml(html);
  if (!safe.trim()) return null;
  return (
    <div
      className={`rich-text-view ${className}`}
      data-testid={testid}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/**
 * Returns true when the string contains at least one HTML tag (and so should
 * be rendered with `RichTextView`). Plain-text legacy values fail this check
 * and should be rendered with `linkify` / `whitespace-pre-wrap`.
 */
export function looksLikeHtml(s) {
  if (!s) return false;
  return /<\/?[a-z][\s\S]*>/i.test(String(s));
}
